<template>
  <div id="pageVehiculoUbicacionHistory">
    <!-- Tarjeta principal -->
    <v-card class="location-history-card">
      <!-- Encabezado con título y filtros -->
      <v-toolbar color="primary" dark flat :dense="$vuetify.breakpoint.smAndDown">
        <v-icon class="mr-2" :size="$vuetify.breakpoint.smAndDown ? 20 : 24">mdi-map-marker-path</v-icon>
        <v-toolbar-title :class="{ 'text-body-2': $vuetify.breakpoint.smAndDown }">
          <span v-if="$vuetify.breakpoint.mdAndUp">Historial de ubicaciones y procesos</span>
          <span v-else>Historial</span>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon @click="toggleFullscreen" v-on="on" :small="$vuetify.breakpoint.smAndDown">
              <v-icon :size="$vuetify.breakpoint.smAndDown ? 20 : 24">{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
            </v-btn>
          </template>
          <span>{{ isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa' }}</span>
        </v-tooltip>
      </v-toolbar>

      <!-- Contenido principal -->
      <v-card-text :class="{ 'pa-0': viewMode === 'table', 'pa-1': $vuetify.breakpoint.smAndDown && viewMode !== 'table' }">
        <!-- Mensaje si no hay datos -->
        <div v-if="!fields || Object.keys(fields).length === 0 || items.length === 0" class="text-center py-8">
          <v-icon :size="$vuetify.breakpoint.smAndDown ? 48 : 64" color="grey lighten-2">mdi-map-marker-off</v-icon>
          <div :class="['mt-2', 'grey--text', 'text--darken-1', $vuetify.breakpoint.smAndDown ? 'text-caption' : '']">
            No hay datos de ubicaciones disponibles.
          </div>
        </div>
        <div>
          <v-data-table 
            :headers="headers" 
            :items="items" 
            :loading="loading"
            :footer-props="footer" 
            class="elevation-0" 
            :items-per-page="10"
            :item-class="getRowClass" 
            :sort-by="[]" 
            :sort-desc="[]" 
            disable-sort
            :dense="$vuetify.breakpoint.smAndDown"
            :mobile-breakpoint="0"
          >
            <template v-slot:item.alert="{ item }">
              <div class="d-flex align-center">
                <v-icon v-if="item.alert" color="red" small class="mr-1">mdi-alert-circle-outline</v-icon>
                
                <!-- Triángulo azul para información adicional -->
                <v-tooltip bottom v-if="item.additional_info && item.additional_info.trim() !== ''">
                  <template v-slot:activator="{ on }">
                    <v-icon color="blue" small v-on="on" class="mr-1">mdi-alert</v-icon>
                  </template>
                  <span>Información adicional disponible</span>
                </v-tooltip>
                
                <!-- Triángulo rojo para daños -->
                <v-tooltip bottom v-if="item.vehicle_status && item.vehicle_status === 'damages'">
                  <template v-slot:activator="{ on }">
                    <v-icon color="red" small v-on="on" class="mr-1">mdi-alert</v-icon>
                  </template>
                  <span>Tiene daños reportados</span>
                </v-tooltip>
                
                <!-- Triángulo naranja para equipamiento faltante -->
                <v-tooltip bottom v-if="item.equipment_status && item.equipment_status.some(eq => eq.is_available === false)">
                  <template v-slot:activator="{ on }">
                    <v-icon color="orange" small v-on="on">mdi-alert</v-icon>
                  </template>
                  <span>Falta {{ item.equipment_status.filter(eq => eq.is_available === false).length }} equipamiento(s) básico(s)</span>
                </v-tooltip>
              </div>
            </template>

            <!-- Columna de fecha -->
            <template v-slot:item.datetime="{ item }">
              <div class="d-flex flex-column">
                <div :class="['font-weight-medium', $vuetify.breakpoint.smAndDown ? 'text-caption' : '']">
                  {{ formatDate(item.delivery_date ? item.delivery_date : item.datetime) }}
                </div>
              </div>
            </template>

            <!-- Columna de tipo -->
            <template v-slot:item.type="{ item }">
              <v-chip 
                x-small 
                label 
                :color="item.type === 'assignment' ? 'teal' : item.type === 'estado_proceso' ? 'orange' : 'indigo'"
                text-color="white"
                class="font-weight-medium"
              >
                <v-icon left x-small>
                  {{ 'mdi-truck-outline' }}
                </v-icon>
                <span v-if="$vuetify.breakpoint.mdAndUp">
                  {{ item.type === 'assignment' ? 'Cesión' : item.type === 'estado_proceso' ? 'Proceso' : 'Movimiento' }}
                </span>
              </v-chip>
            </template>

            <!-- Columna de origen -->
            <template v-slot:item.sourceLocation.name="{ item }">
              <template v-if="shouldHideSourceLocation(item)">
                <span class="grey--text" :class="{ 'text-caption': $vuetify.breakpoint.smAndDown }">-------------</span>
              </template>
              <template v-else>
                <div class="d-flex flex-column align-start">
                  <v-chip 
                    outlined 
                    label 
                    color="blue-grey" 
                    class="font-weight-medium mb-1" 
                    :x-small="$vuetify.breakpoint.smAndDown"
                    :style="{ height: $vuetify.breakpoint.smAndDown ? '16px' : '18px', fontSize: $vuetify.breakpoint.smAndDown ? '0.65rem' : '0.75rem' }"
                  >
                    {{ item.sourceLocation ? item.sourceLocation.name : 'No especificada' }}
                  </v-chip>
                  <!-- Solo mostrar estado si es un cambio de estado de proceso -->
                  <template v-if="item.type === 'estado_proceso'">
                    <v-chip 
                      v-if="item.estadoProcesoSourceLocation" 
                      label 
                      :color="item.estadoProcesoSourceLocation.button_color" 
                      :text-color="item.estadoProcesoSourceLocation.text_color" 
                      class="font-weight-medium" 
                      :x-small="$vuetify.breakpoint.smAndDown"
                      :style="{ height: $vuetify.breakpoint.smAndDown ? '16px' : '18px', fontSize: $vuetify.breakpoint.smAndDown ? '0.65rem' : '0.75rem' }"
                    >
                      {{ item.estadoProcesoSourceLocation.name }}
                    </v-chip>
                    <v-chip 
                      v-else 
                      label 
                      color="grey" 
                      text-color="white" 
                      class="font-weight-medium" 
                      :x-small="$vuetify.breakpoint.smAndDown"
                      :style="{ height: $vuetify.breakpoint.smAndDown ? '16px' : '18px', fontSize: $vuetify.breakpoint.smAndDown ? '0.65rem' : '0.75rem' }"
                    >
                      Sin Estado
                    </v-chip>
                  </template>
                </div>
              </template>
            </template>

            <!-- Columna de destino -->
            <template v-slot:item.destinationLocation.name="{ item }">
              <div class="d-flex flex-column align-start">
                <v-chip 
                  label 
                  color="primary" 
                  text-color="white" 
                  class="font-weight-medium mb-1" 
                  :x-small="$vuetify.breakpoint.smAndDown"
                  :style="{ height: $vuetify.breakpoint.smAndDown ? '16px' : '18px', fontSize: $vuetify.breakpoint.smAndDown ? '0.65rem' : '0.75rem' }"
                >
                  {{ item.destinationLocation ? item.destinationLocation.name : 'No especificada' }}
                </v-chip>
                <!-- Solo mostrar estado si es un cambio de estado de proceso -->
                <template v-if="item.type === 'estado_proceso'">
                  <v-chip 
                    v-if="item.estadoProcesoDestinationLocation" 
                    label 
                    :color="item.estadoProcesoDestinationLocation.button_color" 
                    :text-color="item.estadoProcesoDestinationLocation.text_color" 
                    class="font-weight-medium" 
                    :x-small="$vuetify.breakpoint.smAndDown"
                    :style="{ height: $vuetify.breakpoint.smAndDown ? '16px' : '18px', fontSize: $vuetify.breakpoint.smAndDown ? '0.65rem' : '0.75rem' }"
                  >
                    {{ item.estadoProcesoDestinationLocation.name }}
                  </v-chip>
                  <v-chip 
                    v-else 
                    label 
                    color="grey" 
                    text-color="white" 
                    class="font-weight-medium" 
                    :x-small="$vuetify.breakpoint.smAndDown"
                    :style="{ height: $vuetify.breakpoint.smAndDown ? '16px' : '18px', fontSize: $vuetify.breakpoint.smAndDown ? '0.65rem' : '0.75rem' }"
                  >
                    Sin Estado
                  </v-chip>
                </template>
              </div>
            </template>

            <!-- Columna de conductor/transporte -->
            <template  v-slot:item.transportation="{ item }" >
              <template v-if="item.type !== 'estado_proceso'">
                <div :class="{ 'text-caption': $vuetify.breakpoint.smAndDown }">
                  {{ item.transportation }}
                </div>
                <div :class="['caption', 'grey--text', $vuetify.breakpoint.smAndDown ? 'text-caption' : '']" v-if="item.driver">
                  {{ item.driver.name }} {{ item.driver.lastname }}
                </div>
                <div :class="['caption', 'grey--text', $vuetify.breakpoint.smAndDown ? 'text-caption' : '']" v-else-if="item.transportation === 'GRUA'">
                  {{ (item.crane && item.crane.company) ? item.crane.company : '----' }}
                </div>
              </template>
              <template v-else>
                <div :class="{ 'text-caption': $vuetify.breakpoint.smAndDown }">
                  {{ (item.transportation && item.transportation.name) ? item.transportation.name : '----' }} 
                  {{ (item.transportation && item.transportation.lastname) ? item.transportation.lastname : '----' }}
                </div>
              </template>
            </template>

            <template v-slot:item.action="{ item }">
              <div class="d-inline-flex action-buttons">
                <v-tooltip top v-if="item.type !== 'estado_proceso'">
                  <template v-slot:activator="{ on }">
                    <v-btn icon small color="info"
                      :to="{ name: 'MovimientoDetail', params: { movimiento_id: item.id } }" v-on="on">
                      <v-icon>mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>Ver detalles del movimiento</span>
                </v-tooltip>
                
                <v-tooltip top v-if="item.type === 'estado_proceso'">
                  <template v-slot:activator="{ on }">
                    <v-btn icon small color="info"
                      :to="{ name: 'EstadoProcesoDetail', params: { proceso_id: item.id } }" v-on="on">
                      <v-icon>mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>Ver detalles del proceso</span>
                </v-tooltip>
              </div>
            </template>
          </v-data-table>
        </div>

        <!-- Indicador de carga -->
        <div v-if="loading" class="loading-overlay">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import moment from 'moment-timezone'
// import VehiculoService from '@/services/MovimientoService'
import VehiculoService from '../../services/VehiculoService'

export default {
  props: {
    fields: {
      type: Object,
      default: () => ({})
    }
  },

  data: () => ({
    loading: false,
    totalItems: 0,
    items: [],
    viewMode: 'timeline', // 'timeline' o 'table'
    isFullscreen: false,

    headers: [
      { text: '', value: 'alert', width: '5%', sortable: false },
      { text: 'Fecha', value: 'datetime', width: '15%', sortable: false },
      { text: 'Tipo', value: 'type', width: '12%', sortable: false },
      { text: 'Origen', value: 'sourceLocation.name', width: '18%', sortable: false },
      { text: 'Destino', value: 'destinationLocation.name', width: '18%', sortable: false },
      { text: 'Responsable', value: 'transportation', width: '15%', sortable: false },
      { text: 'Acciones', value: 'action', sortable: false, align: 'center', width: '10%' }
    ],

    footer: {
      itemsPerPageOptions: [5, 10, 15, 20]
    },

    pagination: {
      page: 1,
      itemsPerPage: 10
    }
  }),

  computed: {
    totalPages () {
      return Math.ceil(this.totalItems / this.pagination.itemsPerPage)
    },

    sortedItems () {
      // Ordenar los items para la vista de timeline (más recientes primero)
      return [...this.items].sort((a, b) => b.datetime - a.datetime)
    }
  },

  methods: {
    toggleFullscreen () {
      const element = document.getElementById('pageVehiculoUbicacionHistory')
      
      if (!this.isFullscreen) {
        // Entrar en pantalla completa
        if (element.requestFullscreen) {
          element.requestFullscreen()
        } else if (element.mozRequestFullScreen) { // Firefox
          element.mozRequestFullScreen()
        } else if (element.webkitRequestFullscreen) { // Chrome, Safari y Opera
          element.webkitRequestFullscreen()
        } else if (element.msRequestFullscreen) { // IE/Edge
          element.msRequestFullscreen()
        }
      } else {
        // Salir de pantalla completa
        if (document.exitFullscreen) {
          document.exitFullscreen()
        } else if (document.mozCancelFullScreen) { // Firefox
          document.mozCancelFullScreen()
        } else if (document.webkitExitFullscreen) { // Chrome, Safari y Opera
          document.webkitExitFullscreen()
        } else if (document.msExitFullscreen) { // IE/Edge
          document.msExitFullscreen()
        }
      }
    },

    handleFullscreenChange () {
      // Detectar cambios en el estado de pantalla completa
      this.isFullscreen = !!(
        document.fullscreenElement ||
        document.mozFullScreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      )
    },

    getRowClass (item) {
      // Agregar clase especial para filas de estado de proceso
      return item.type === 'estado_proceso' ? 'estado-proceso-row' : ''
    },
    
    formatDateTime (timestamp) {
      if (!timestamp) return 'N/A'
      return moment.unix(timestamp).tz('Europe/Madrid').format('DD/MM/YYYY HH:mm')
    },
    shouldHideSourceLocation (item) {
      // Para cambios de estado de proceso, no ocultar origen (es la misma ubicación pero es relevante)
      if (item.type === 'estado_proceso') {
        return false
      }
      
      return (
        // Comprobar si es movimiento de tipo entry
        item.type === 'entry' &&
        item.sourceLocation &&
        item.destinationLocation &&
        item.sourceLocation.id === item.destinationLocation.id
      )
    },

    formatDate (timestamp) {
      if (!timestamp) return 'N/A'
      return moment.unix(timestamp).tz('Europe/Madrid').format('DD/MM/YYYY HH:mm')
    },

    formatTime (timestamp) {
      if (!timestamp) return ''
      return moment.unix(timestamp).format('HH:mm')
    },

    getStatusColor (status) {
      const statusColorMap = {
        'unreported': 'red',
        'reported': 'orange',
        'transport': 'blue',
        'processing': 'purple',
        'completed': 'green'
      }
      return statusColorMap[status] || 'grey'
    },

    getStatusIcon (status) {
      const statusIconMap = {
        'unreported': 'mdi-alert-circle-outline',
        'reported': 'mdi-bell-ring-outline',
        'transport': 'mdi-truck-outline',
        'processing': 'mdi-progress-clock',
        'completed': 'mdi-check-circle-outline'
      }
      return statusIconMap[status] || 'mdi-help-circle-outline'
    },

    translateStatus (status) {
      const statusMap = {
        'unreported': 'No reportado',
        'reported': 'Reportado',
        'transport': 'Recepcionado',
        'processing': 'En proceso',
        'completed': 'Completado'
      }
      return statusMap[status] || status
    },

    async loadData () {
      if (!this.fields || !this.fields.id) return

      this.loading = true
      try {
        // Configurar los parámetros básicos
        const params = {
          id: this.fields.id
        }

        const response = await VehiculoService.view(params)
            
        // Combinar movimientos y cambios de estado de proceso
        const movements = response.data.data.movementsWithLocations || []
        const estadoProcesos = response.data.data.vehicleLocationEstadoProcesos || []
        
        // Filtrar movimientos completados y normalizar fechas
        const completedMovements = movements
          .filter(mov => mov.status === 'completed')
          .map(mov => {
            // Normalizar datetime a timestamp en segundos
            let datetime = 0
            if (mov.datetime) {
              if (typeof mov.datetime === 'string') {
                // Convertir string a timestamp en segundos
                datetime = Math.floor(new Date(mov.datetime).getTime() / 1000)
              } else if (typeof mov.datetime === 'number') {
                // Verificar si está en milisegundos o segundos
                // Los timestamps en segundos son menores a 10000000000 (16 Nov 2286)
                // Los timestamps en milisegundos son mayores a 1000000000000 (9 Sep 2001)
                datetime = mov.datetime > 10000000000
                  ? Math.floor(mov.datetime / 1000) // Convertir de milisegundos a segundos
                  : mov.datetime // Ya está en segundos
              }
            }
            
            return {
              ...mov,
              datetime: datetime // Ya tiene fallback a 0
            }
          })
        
        // Transformar estados de proceso para que tengan la misma estructura que los movimientos
        const transformedEstadoProcesos = estadoProcesos.map(estado => {
          // Convertir fecha correctamente usando created_at
          let datetime = 0
          if (estado.created_at) {
            if (typeof estado.created_at === 'string') {
              // Si viene como string de fecha, convertir a timestamp en segundos
              datetime = Math.floor(new Date(estado.created_at).getTime() / 1000)
            } else if (typeof estado.created_at === 'number') {
              // Si ya es número, verificar si está en milisegundos o segundos
              // Los timestamps en segundos son menores a 10000000000 (16 Nov 2286)
              // Los timestamps en milisegundos son mayores a 1000000000000 (9 Sep 2001)
              datetime = estado.created_at > 10000000000
                ? Math.floor(estado.created_at / 1000) // Convertir de milisegundos a segundos
                : estado.created_at // Ya está en segundos
            }
          }
          
          return {
            ...estado,
            type: 'estado_proceso',
            datetime: datetime, // Ya tiene fallback a 0
            transportation: estado.responsable,
            sourceLocation: estado.location,
            destinationLocation: estado.location,
            estadoProcesoSourceLocation: estado.lastEstadoProceso,
            estadoProcesoDestinationLocation: estado.estadoProceso
          }
        })
        
        // Combinar ambos arrays y ordenar por fecha descendente (más recientes primero)
        this.items = [...completedMovements, ...transformedEstadoProcesos]
          .sort((a, b) => {
            // Usar delivery_date si existe (para movimientos), sino datetime
            const dateA = a.delivery_date || a.datetime || 0
            const dateB = b.delivery_date || b.datetime || 0
            return dateB - dateA // Ordenar descendente (más reciente primero)
          })

        // Si el API soporta paginación, actualizar el total de items
        if (response.data.meta && response.data.meta.pagination) {
          this.totalItems = response.data.meta.pagination.total
        } else {
          this.totalItems = this.items.length
        }
      } catch (error) {
        console.error('Error al cargar el historial de movimientos:', error)
      } finally {
        this.loading = false
      }
    }
  },

  watch: {
    'fields.id': {
      handler () {
        this.loadData()
      },
      immediate: true
    }
  },

  created () {
    // Ya no necesitamos crear aquí porque se maneja en el watcher de fields.id
    
    // Agregar listeners para detectar cambios en pantalla completa
    document.addEventListener('fullscreenchange', this.handleFullscreenChange)
    document.addEventListener('mozfullscreenchange', this.handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange)
    document.addEventListener('msfullscreenchange', this.handleFullscreenChange)
  },

  beforeDestroy () {
    // Limpiar listeners al destruir el componente
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('msfullscreenchange', this.handleFullscreenChange)
  }
}
</script>

<style scoped>
#pageVehiculoUbicacionHistory {
  width: 100%;
  height: 100%;
}

/* Estilos para cuando está en pantalla completa */
#pageVehiculoUbicacionHistory:-webkit-full-screen {
  width: 100%;
  height: 100%;
  background-color: white;
  overflow: auto;
}

#pageVehiculoUbicacionHistory:-moz-full-screen {
  width: 100%;
  height: 100%;
  background-color: white;
  overflow: auto;
}

#pageVehiculoUbicacionHistory:-ms-fullscreen {
  width: 100%;
  height: 100%;
  background-color: white;
  overflow: auto;
}

#pageVehiculoUbicacionHistory:fullscreen {
  width: 100%;
  height: 100%;
  background-color: white;
  overflow: auto;
}

.location-history-card {
  position: relative;
  overflow: hidden;
}

.v-timeline-item__body {
  padding-right: 12px;
}

/* Para espaciar los elementos de la timeline */
.v-timeline-item {
  padding-bottom: 12px;
}

/* Quitar padding excesivo en tarjetas de timeline */
.v-timeline-item .v-card__title {
  padding: 12px 16px;
}

.v-timeline-item .v-card__text {
  padding: 0 16px 8px;
}

.v-timeline-item .v-card__actions {
  padding: 0 8px;
}

/* Overlay de carga */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

/* Estilos para el modo de tabla */
.v-data-table ::v-deep .v-data-table__wrapper {
  border-radius: 0;
}

/* Transición entre modos de vista */
.v-card__text {
  transition: padding 0.3s ease;
}

/* Mayor separación para registros de estado de proceso */
::v-deep .estado-proceso-row {
  height: 80px;
}

::v-deep .estado-proceso-row td {
  padding-top: 20px !important;
  padding-bottom: 20px !important;
  background-color: #fafafa !important;
}

/* Agregar espacio antes de las filas de estado proceso */
::v-deep tbody tr:not(.estado-proceso-row) + .estado-proceso-row td {
  padding-top: 50px !important;
}

/* Opcional: efecto visual adicional */
::v-deep .estado-proceso-row:hover td {
  background-color: #f0f0f0 !important;
}
</style>
